import { useCallback, useEffect, useState } from "react";
import { tr } from "../i18n/tr";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { ListSkeleton } from "../components/Skeletons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAppConfig } from "../hooks/useAppConfig";
import {
  GraduationCap, Loader2, ArrowRight, Calendar, Users, Clock, MapPin,
  Sparkles, BookOpen, Eye, EyeOff, Search,
} from "lucide-react";
import { toast } from "sonner";
import { useSecretMode, Hidden } from "../contexts/SecretModeContext";
import LowScoreAlertCard from "../components/formateur/LowScoreAlertCard";

export default function FormateurSessionsPage() {
  const { branding } = useAppConfig();
  const { axiosAuth, user } = useAuth();
  const navigate = useNavigate();
  const api = axiosAuth();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [q, setQ] = useState("");
  const { enabled: secret, toggle: toggleSecret } = useSecretMode();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/formateur/sessions");
      setSessions(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = (sessions || []).filter((s) => !q || (s.title || "").toLowerCase().includes(q.toLowerCase()) || (s.program_title || "").toLowerCase().includes(q.toLowerCase()));
  const totalStudents = sessions.reduce((a, s) => a + (s.enrollments_count || 0), 0);
  const avgProgress = sessions.length ? Math.round(sessions.reduce((a, s) => a + (s.my_progress || 0), 0) / sessions.length) : 0;

  return (
    <div className="min-h-screen" style={{ background: "radial-gradient(circle at 0% 0%, #1a1130 0%, #0b0811 60%)" }} data-testid="formateur-sessions-page">
      {/* HERO */}
      <div className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          background: "radial-gradient(circle at 80% 30%, var(--brand-primary, #f7c935) 0%, transparent 45%)"
        }} />
        <div className="relative max-w-6xl mx-auto px-6 py-10 lg:py-14">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold mb-3" style={{ color: "var(--brand-primary, #f7c935)" }}>
                {branding.logo_url ? <img src={branding.logo_url} alt={branding.app_name} className="h-6 object-contain" data-testid="trainer-brand-logo" /> : <Sparkles className="w-3.5 h-3.5" />} {branding.app_name || tr("lms.espace_formateur_7afe", "Espace formateur")}
              </div>
              <div className="mb-3" data-testid="lms-language-switcher"><LanguageSwitcher /></div>
              <h1 className="text-4xl lg:text-6xl font-black tracking-tighter leading-none text-white" data-testid="trainer-welcome-title">
                {branding.welcome_title ? branding.welcome_title : <>Bonjour {user?.name?.split(" ")[0] || "Formateur"}, <span style={{ color: "var(--brand-primary, #f7c935)" }}>animons</span>.</>}
              </h1>
              <p className="text-slate-300 mt-3 text-sm lg:text-base max-w-xl" data-testid="trainer-welcome-text">
                {branding.welcome_text || tr("lms.animez_vos_sessions_en_7370", "Animez vos sessions en direct. Votre progression debloque les modules de vos stagiaires.")}
              </p>
            </div>
            <button
              onClick={toggleSecret}
              className={"flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-lg border transition " + (secret ? "bg-[var(--brand-primary,#f7c935)]/15 border-[var(--brand-primary,#f7c935)]/40 text-[var(--brand-primary,#f7c935)]" : "bg-white/5 border-white/10 text-slate-300 hover:text-white")}
              title={secret ? "Desactiver le mode secret" : tr("lms.activer_le_mode_secret_9146", "Activer le mode secret (flou des infos sensibles)")}
              data-testid="secret-mode-toggle"
            >
              {secret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              Mode secret {secret ? "actif" : "desactive"}
            </button>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            <KPI icon={BookOpen} label={tr("lms.sessions_a9c4", "Sessions")} value={sessions.length} />
            <KPI icon={Users} label={tr("lms.stagiaires_11bb", "Stagiaires")} value={totalStudents} sensitive />
            <KPI icon={Sparkles} label={tr("lms.ma_progression_4cd8", "Ma progression")} value={avgProgress + "%"} accent />
            <KPI icon={GraduationCap} label={tr("lms.role_bbba", "Role")} value="Formateur" />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={tr("lms.rechercher_une_session_05f6", "Rechercher une session...")}
              className="w-full pl-10 pr-3 py-2.5 bg-[#14101c] border border-white/10 rounded-xl text-sm text-white placeholder:text-slate-500 focus:border-[var(--brand-primary,#f7c935)] outline-none"
              data-testid="formateur-search"
            />
          </div>
        </div>

        {/* Iter 235c — Alertes apprenants à adapter (score quiz faible) */}
        <LowScoreAlertCard limit={3} className="mb-6" />

        {loading ? (
          <div className="py-6"><ListSkeleton items={3} /></div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-white/10 rounded-2xl bg-[#14101c]/40">
            <GraduationCap className="w-10 h-10 mx-auto mb-3 text-slate-700" />
            <div className="font-bold text-white mb-1">{tr("lms.aucune_session_assignee_5896", "Aucune session assignee")}</div>
            <p className="text-xs text-slate-500">{tr("lms.demandez_a_un_administrateur_b36b", "Demandez a un administrateur de vous affecter comme formateur sur une session.")}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((s) => <SessionCard key={s.session_id} s={s} onOpen={() => navigate("/formateur/sessions/" + s.session_id)} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent = false, sensitive = false }) {
  const content = (
    <>
      <Icon className="w-5 h-5 mb-2" style={{ color: accent ? "var(--brand-primary, #f7c935)" : "#cbd5e1" }} />
      <div className="text-2xl font-black text-white leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1 font-bold">{label}</div>
    </>
  );
  return (
    <div className={"border rounded-2xl p-4 " + (accent ? "border-[var(--brand-primary,#f7c935)]/40 bg-[var(--brand-primary,#f7c935)]/5" : "border-white/10 bg-[#14101c]/60")}>
      {sensitive ? <Hidden as="div">{content}</Hidden> : content}
    </div>
  );
}

function SessionCard({ s, onOpen }) {
  const pct = s.my_progress || 0;
  return (
    <button onClick={onOpen}
      data-testid={"trainer-sess-" + s.session_id}
      className="group text-left bg-[#14101c] border border-white/10 rounded-2xl overflow-hidden hover:border-[var(--brand-primary,#f7c935)]/40 hover:-translate-y-1 transition-all shadow-lg flex flex-col"
    >
      <div className="relative h-28" style={{ background: "linear-gradient(135deg, var(--brand-secondary, #461d8a) 0%, #0b0811 100%)" }}>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <span className="absolute top-3 right-3 text-[9px] px-2 py-1 rounded-full bg-blue-500/30 text-blue-200 font-bold uppercase">{tr("lms.formateur_1517", "Formateur")}</span>
        <div className="absolute bottom-3 left-4 right-4">
          <div className="text-[10px] uppercase tracking-wider text-white/80 font-bold mb-1 line-clamp-1">{s.program_title}</div>
          <h3 className="font-black text-white text-base leading-tight line-clamp-2">{s.title || s.program_title}</h3>
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="space-y-1 text-[11px] text-slate-400 mb-4">
          <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {s.start_date} → {s.end_date}</div>
          {s.location && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {s.location}</div>}
          <Hidden as="div" className="flex items-center gap-1.5"><Users className="w-3 h-3" /> {s.enrollments_count || 0} stagiaire{(s.enrollments_count || 0) > 1 ? "s" : ""}</Hidden>
        </div>
        <div className="mt-auto pt-3 border-t border-white/5">
          <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1.5">
            <span className="uppercase tracking-wider font-bold">{tr("lms.ma_progression_4cd8", "Ma progression")}</span>
            <span className="text-white font-black text-sm">{pct}%</span>
          </div>
          <div className="bg-white/5 rounded-full h-1.5 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: pct + "%", background: "var(--brand-primary, #f7c935)" }} />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between text-xs">
          <span className="text-slate-500 font-semibold group-hover:text-[var(--brand-primary,#f7c935)] transition-colors">{tr("lms.animer_la_session_e02b", "Animer la session")}</span>
          <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-[var(--brand-primary,#f7c935)] group-hover:translate-x-0.5 transition-all" />
        </div>
      </div>
    </button>
  );
}
